/**
 * The MMM half of `mediaIntelligenceService`: the funnel, stage and budget
 * fetchers, and the DEMO_MODE figures behind them.
 *
 * EXTRACTED, NOT REDESIGNED. This was the MMM (MARKETING MIX MODELING)
 * FUNCTIONS section plus the MMM part of the MOCK DATA section of
 * `mediaIntelligenceService.ts`, which stood at 3,270 lines against this repo's
 * 800-line rule. Same names, same figures, same comments.
 *
 * THE MOCKS CAME WITH IT ON PURPOSE. `fetchMMMStatus` and
 * `fetchMMMRecommendations` fall back to `getMockMMMStatus` and
 * `getMockMMMRecommendations` under DEMO_MODE, so leaving those behind would
 * have made this module import from the service while the service imports these
 * two fetchers back for its default-export barrel — a cycle. What both halves
 * genuinely share went to `./mediaIntelligenceShared` instead.
 *
 * NOT A MODEL. Everything here is either a database read or a stated constant;
 * the real Bayesian fit lives in `mmm-service/` and reaches the app through
 * `services/mmm/`. `MMM_CHANNEL_STRATEGY` below is a demo narrative and says so.
 */
import { getSupabaseClient } from '@/config';
import { DEMO_MODE } from '@/config/demoConfig';
import { todayDataDate } from './dataDate';
import { formatConversions } from '@/utils/format';
import { REALLOC_STEP } from './insights/insightConstants';
import type { TFn } from '@/utils/channelCorrelation';
import { logError } from './logger';
import { plausibleRevenue, placeholderRevenue } from '@/components/mmm/unmeasured';
import { PLATFORM_LABEL as PLATFORM_NAME, platformShortLabel } from '@/utils/platformLabel';
import { DEFAULT_SHARED } from './dashboardFilters';
import { PLATFORM_TILES, type TileSpec } from './channels/platformTiles';
import {
  ALL_TIME_START,
  periodToRange,
  readCampaignSummary,
  type Scope,
} from './mediaIntelligenceShared';
import type {
  MMMStatus,
  MMMStageBreakdown,
  MMMBudgetItem,
  MMMRecommendationsData,
  DataQualityAction,
} from './mediaIntelligenceTypes';

// The three modules lifted out of this file.
//
// Only fetchLatestMetricDate is re-exported: it has an external consumer
// (src/hooks/useReportingPeriod.ts) that reaches it through the service barrel.
// The other eight names are module-private today, and mediaIntelligenceService
// does `export * from './mediaIntelligenceMmm'`, so re-exporting them would
// publish MMM demo fabrications and internal stage tables app-wide.
export { fetchLatestMetricDate } from './mediaIntelligenceMmmCoverage';

import {
  OBJECTIVE_STAGE, STAGE_VOLUME, STAGE_VOLUME_LABEL, emptyStage, emptyRecommendations,
} from './mediaIntelligenceMmmStages';
import { fetchLatestMetricDate, fetchMetricDaysCovered } from './mediaIntelligenceMmmCoverage';
import { getMockMMMStatus, getMockMMMRecommendations } from './mediaIntelligenceMmmDemo';

/** D2 is the one native page that shows a platform's conversion value, on the tabs whose registry carries a ROAS or Conv. value tile. */
const TILES_BY_PLATFORM: Readonly<Record<string, readonly TileSpec[] | undefined>> = PLATFORM_TILES;
const showsValue = (platform: string): boolean =>
  TILES_BY_PLATFORM[platform]?.some((s) => s.metric === 'roas' || s.metric === 'conversionValue') ?? false;
const VALUE_TABS = Object.keys(PLATFORM_TILES).filter(showsValue).map(platformShortLabel).join(' / ');

/**
 * MOCK ONLY — this does not read the database.
 *
 * It used to call getSupabaseClient() and then ignore the result, returning the
 * mock down every branch including the try. That made it read like a data path
 * in review while never querying anything. The pretence is gone; the behaviour
 * is unchanged.
 *
 * Wiring it to real data is Phase 1 of the MMM plan, and it will need a `scope`
 * argument — there is none today, so the result cannot be workspace-specific.
 * Until then /mmm carries an "Illustrative sample" notice.
 */
/**
 * Real data-readiness for the MMM surface, replacing a mock that reported a
 * fitted Bayesian model (dataDays 87, R-Squared 0.87, MAPE 12.3%) on every
 * workspace. Nothing is fitted, so nothing about fit is reported; what IS
 * measurable is how much history the workspace actually has.
 */
/**
 * `t` is a parameter rather than a hook because this runs outside React. The
 * rows it builds are read on four pages and quoted into the MMM report prompt,
 * so they have to follow the reader's language rather than the code's.
 */
/**
 * `strict`: the two reads below that are otherwise logged and swallowed (the
 * revenue probe, the latest metric date) throw instead. The /looker data-quality
 * box asks for it, because a swallowed failure there reads as "nothing to fix".
 * /mmm and the dashboard keep the default.
 */
export const fetchMMMStatus = async (
  scope: Scope,
  t: TFn,
  opts: { strict?: boolean } = {},
): Promise<MMMStatus> => {
  const supabase = getSupabaseClient();
  if (!supabase || DEMO_MODE) return getMockMMMStatus();

  const today = todayDataDate();
  const { daysWithData, firstDate } = await fetchMetricDaysCovered(scope, ALL_TIME_START, today);

  // Same rule and same source as the page's revenueAvailable, deliberately: an
  // existence check on conversion_value > 0 said "Revenue model" for a workspace
  // carrying ฿1,268 of value against ฿962,392 of spend, while /mmm said revenue
  // was unsynced. Two predicates that disagree is how a meaningless ratio gets
  // shown once someone edits one of them.
  const { rows: summary, error: summaryError } = await readCampaignSummary(
    scope, ALL_TIME_START, today, 'fetchMMMStatus revenue probe',
  );
  // Logged, not thrown, and the outcome is unchanged: a failed read still lands
  // on hasRevenue = false, which reads on /mmm as "conversion value not synced".
  // That is a claim about the account made from a refusal — the same shape of
  // lie this file calls out elsewhere — but raising here would take down the
  // whole status panel over a probe. Said out loud so it is findable.
  if (summaryError && opts.strict) {
    const reason = (summaryError as { message?: unknown }).message;
    throw new Error(typeof reason === 'string' ? reason : 'Revenue probe read failed');
  }
  if (summaryError) {
    logError('MediaIntelligenceService', 'Revenue probe read failed; reporting no revenue', summaryError);
  }
  const adRows = summary.filter((r) => r.platform !== 'analytics');
  const totalSpend = adRows.reduce((sum, r) => sum + Number(r.total_spend || 0), 0);
  const totalValue = adRows.reduce((sum, r) => sum + Number(r.total_value || 0), 0);
  const hasRevenue = plausibleRevenue(totalValue, totalSpend);

  const spanDays = firstDate
    ? Math.max(
        1,
        Math.round((new Date(today).getTime() - new Date(firstDate).getTime()) / 86_400_000) + 1,
      )
    : 0;

  const latest = await fetchLatestMetricDate(scope, opts);
  const staleDays = latest
    ? Math.max(0, Math.round((new Date(today).getTime() - new Date(latest).getTime()) / 86_400_000))
    : 0;

  // Per channel, so the reader is told which account to open rather than that
  // "the ad platforms" are wrong. Ordered by what blocks what: telling someone
  // their conversion value is a placeholder while nothing at all has synced puts
  // the second problem first, and the /mmm empty state shows only the first.
  const perChannel = new Map<string, { spend: number; conversions: number; value: number }>();
  for (const r of adRows) {
    const cur = perChannel.get(r.platform) ?? { spend: 0, conversions: 0, value: 0 };
    cur.spend += Number(r.total_spend || 0);
    cur.conversions += Number(r.total_conversions || 0);
    cur.value += Number(r.total_value || 0);
    perChannel.set(r.platform, cur);
  }
  const baht = (n: number): string => `฿${Math.round(n).toLocaleString()}`;

  // Each finding names its window: the day count, the span and the per-channel totals are read from ALL_TIME_START
  // to today (allTime); only the staleness is the state today (asOfToday).
  const dataQuality: DataQualityAction[] = [];

  if (daysWithData === 0) {
    dataQuality.push({
      finding: t('dataQuality.noSyncFinding'),
      action: t('dataQuality.noSyncAction'),
      unlocks: t('dataQuality.noSyncUnlocks'),
      severity: 'blocking',
      window: 'allTime',
    });
  }

  // A channel whose tab shows no value is told what fixing it gets: a stored value its tab does not show yet.
  const unshown = {
    page: t('nav.channelDeepDive'), tabs: VALUE_TABS, value: t('channels.tile.conversionValue'), roas: t('channels.tile.roas'),
  };
  for (const [platform, m] of perChannel) {
    const name = PLATFORM_NAME[platform] ?? platform;
    if (m.spend <= 0) continue;

    if (m.conversions === 0) {
      dataQuality.push({
        channel: name,
        finding: t('dataQuality.noConvFinding', { spend: baht(m.spend) }),
        action: t('dataQuality.noConvAction', { channel: name }),
        unlocks: t('dataQuality.noConvUnlocks', { channel: name }),
        severity: 'blocking',
        window: 'allTime',
      });
      continue;
    }

    // A value far below spend is a placeholder someone set so the platform can
    // optimise, not revenue. Reported per conversion because that is the number
    // the reader will recognise in their own console.
    if (placeholderRevenue(m.value, m.spend)) {
      dataQuality.push({
        channel: name,
        finding: t('dataQuality.placeholderValueFinding', {
          perConv: (m.value / m.conversions).toFixed(2),
          value: baht(m.value),
          spend: baht(m.spend),
        }),
        action: t('dataQuality.placeholderValueAction', { channel: name }),
        unlocks: showsValue(platform)
          ? t('dataQuality.placeholderValueUnlocks', { channel: name })
          : t('dataQuality.placeholderValueUnlocksUnshown', { channel: name, ...unshown }),
        severity: 'degraded',
        window: 'allTime',
      });
    } else if (m.value === 0) {
      dataQuality.push({
        channel: name,
        finding: t('dataQuality.noValueFinding', { conversions: formatConversions(m.conversions) }),
        action: t('dataQuality.noValueAction', { channel: name }),
        unlocks: showsValue(platform)
          ? t('dataQuality.noValueUnlocks', { channel: name })
          : t('dataQuality.noValueUnlocksUnshown', { channel: name, ...unshown }),
        severity: 'degraded',
        window: 'allTime',
      });
    }
  }

  if (daysWithData > 0 && spanDays > 0 && daysWithData < spanDays * 0.8) {
    dataQuality.push({
      finding: t('dataQuality.gapsFinding', { days: daysWithData, span: spanDays }),
      action: t('dataQuality.gapsAction'),
      unlocks: t('dataQuality.gapsUnlocks'),
      severity: 'degraded',
      window: 'allTime',
    });
  }

  if (staleDays > 1) {
    // shortcut: reads the day count off an N-day token ('28d'); a month preset as the default would print NaN, so name its label then.
    const defaultDays = Number.parseInt(DEFAULT_SHARED.period, 10);
    dataQuality.push({
      finding: t('dataQuality.staleFinding', { latest, days: staleDays }),
      action: t('dataQuality.staleAction'),
      unlocks: t('dataQuality.staleUnlocks', { latest, days: defaultDays }),
      severity: staleDays > 30 ? 'blocking' : 'degraded',
      window: 'asOfToday',
    });
  }

  // Kept as flat strings for callers that show a single line (the /mmm empty
  // state), derived so the two can never disagree.
  const nextSteps = dataQuality.map((d) =>
    d.channel
      ? t('dataQuality.nextStepWithChannel', {
          channel: d.channel,
          finding: d.finding,
          action: d.action,
        })
      : `${d.finding} ${d.action}`,
  );

  return {
    // Revenue-based only when there is revenue to base it on.
    kpiType: hasRevenue ? 'revenue' : 'leads',
    dataDays: daysWithData,
    spanDays,
    dataQuality,
    nextSteps,
    // progress / rSquared / mape deliberately absent: see MMMStatus.
  };
};



/**
 * Budget recommendations built from the workspace's own spend and conversions.
 *
 * WHAT IS MEASURED: currentBudget, contribution and roi come straight from
 * get_campaign_summary for the selected period — the same source, window and
 * scope the dashboard uses, so the figures reconcile with the rest of the app.
 *
 * WHAT IS A HEURISTIC: recommendedBudget. The total is held constant and
 * redistributed in proportion to each channel's conversions-per-baht, so the
 * cheapest converter gains and the dearest gives up budget. That is an
 * efficiency reallocation, NOT a marketing-mix model: it has no saturation
 * estimate, no adstock, no control variables, and no counterfactual. A real MMM
 * (ADR-003, Meridian on Cloud Run) is Phase 2, and the page keeps its
 * "illustrative" notice until then.
 *
 * `confidence` reports DATA COVERAGE — the share of days in the window that
 * actually carry rows — not model confidence, which cannot exist without a
 * model. The explanation string says so where a reader will see it.
 */
export const fetchMMMRecommendations = async (
  scope: Scope,
  periodType = 'month',
  // Same reason as fetchMMMStatus: the Key Insight and the "Why" paragraph are
  // the most-read prose on /mmm and are assembled here, outside React.
  // REQUIRED, not defaulted: a default would let a new call site compile and
  // then render whichever language the default happened to encode.
  t: TFn,
): Promise<MMMRecommendationsData> => {
  const supabase = getSupabaseClient();
  if (!supabase || DEMO_MODE) return getMockMMMRecommendations(periodType);

  try {
    const { start, end } = periodToRange(periodType);
    const { rows: data, error } = await readCampaignSummary(
      scope, start, end, 'fetchMMMRecommendations',
    );
    if (error) {
      logError('MediaIntelligenceService', 'Error fetching MMM recommendations', error);
      return emptyRecommendations(t('mmmRec.reasonLoadFailed'), t);
    }

    // Google Analytics is web analytics, not a buyable channel — excluded here
    // for the same reason the dashboard excludes it from blended ad totals.
    const rows = data.filter((r) => r.platform !== 'analytics');
    if (rows.length === 0) {
      return emptyRecommendations(t('mmmRec.reasonNoCampaigns'), t);
    }

    interface Agg {
      spend: number;
      conversions: number;
      value: number;
      stages: Record<
        'aware' | 'consider' | 'convert' | 'unspecified',
        { spend: number; conversions: number; value: number; reach: number; engagements: number }
      >;
    }
    const byPlatform = new Map<string, Agg>();
    for (const r of rows) {
      const agg = byPlatform.get(r.platform) ?? {
        spend: 0,
        conversions: 0,
        value: 0,
          stages: {
          aware: { spend: 0, conversions: 0, value: 0, reach: 0, engagements: 0 },
          consider: { spend: 0, conversions: 0, value: 0, reach: 0, engagements: 0 },
          convert: { spend: 0, conversions: 0, value: 0, reach: 0, engagements: 0 },
          unspecified: { spend: 0, conversions: 0, value: 0, reach: 0, engagements: 0 },
        },
      };
      const spend = Number(r.total_spend || 0);
      const conversions = Number(r.total_conversions || 0);
      const value = Number(r.total_value || 0);
      // Unknown/NULL objective stays visibly unknown ('unspecified'), never a
      // silent 'convert': the audit's F2 found the same NULL-objective money
      // told three different stories across three screens.
      const stage = OBJECTIVE_STAGE[(r.objective || '').toLowerCase()] ?? 'unspecified';
      const reach = Number(r.total_reach || 0);
      // Video views are a consideration signal like engagements; a platform
      // reports one or the other, rarely both, so they share a bucket.
      const engagements = Number(r.total_engagements || 0) + Number(r.total_video_views || 0);
      agg.spend += spend;
      agg.conversions += conversions;
      agg.value += value;
      agg.stages[stage].spend += spend;
      agg.stages[stage].conversions += conversions;
      agg.stages[stage].value += value;
      agg.stages[stage].reach += reach;
      agg.stages[stage].engagements += engagements;
      byPlatform.set(r.platform, agg);
    }

    const totalSpend = [...byPlatform.values()].reduce((sum, a) => sum + a.spend, 0);
    const totalConversions = [...byPlatform.values()].reduce((sum, a) => sum + a.conversions, 0);

    // A channel that spent money and recorded no conversion at all is UNMEASURED,
    // not unproductive: zero here means the account has no conversion tracking
    // configured, and the two are indistinguishable from this side. LINE on the
    // audited workspace spends ฿71,967 across 49,990 clicks and reports cv=0 for
    // every one of its 762 days — its own API returns the zero.
    //
    // Weighting it at 0 and reallocating stripped its budget to ฿0, which reads
    // as "we measured this channel and it does nothing". Unmeasured channels are
    // therefore held at their current budget and flagged, and only the channels
    // that actually report conversions redistribute among themselves.
    const isUnmeasured = (a: Agg): boolean => a.spend > 0 && a.conversions === 0;

    /**
     * Bounded shift toward the cheaper converter — the same rule /insights'
     * pair cards apply, at channel grain.
     *
     * The previous formula, (w / Σw) × measuredSpend, made each channel's
     * budget SHARE equal its efficiency share. That is not "toward the
     * channels converting most cheaply": on the live workspace it recommended
     * +61% for the channel with a 46% WORSE CPA, because that channel's budget
     * was small relative to its efficiency. No optimisation frame produces
     * proportional-to-efficiency: linear response says pour into the cheapest;
     * diminishing returns says equalise marginal CPA (unmeasurable here). The
     * honest middle is a capped step: every measured channel with CPA above
     * the measured-set median donates REALLOC_STEP of its spend, and the
     * donated pool goes to the below-median channels — weighted by their
     * conversions-per-baht so the cheapest gains most. Totals never move, and
     * the simulator is where a reader deliberately goes further.
     */
    const shift = new Map<string, number>(); // recommended − current, per platform
    {
      const measured = [...byPlatform.entries()]
        .filter(([, a]) => !isUnmeasured(a) && a.spend > 0 && a.conversions > 0);
      const cpas = measured.map(([, a]) => a.spend / a.conversions).sort((x, y) => x - y);
      const mid = Math.floor(cpas.length / 2);
      const medianCpa = cpas.length === 0 ? 0
        : cpas.length % 2 ? cpas[mid] : (cpas[mid - 1] + cpas[mid]) / 2;

      const donors = measured.filter(([, a]) => a.spend / a.conversions > medianCpa);
      const receivers = measured.filter(([, a]) => a.spend / a.conversions < medianCpa);
      // At the median, or with no one on the other side, nothing moves — a
      // one-channel or perfectly-tied set has no trade to recommend.
      if (donors.length > 0 && receivers.length > 0) {
        let pool = 0;
        for (const [platform, a] of donors) {
          const donated = a.spend * REALLOC_STEP;
          shift.set(platform, -donated);
          pool += donated;
        }
        const receiverWeight = receivers.reduce((sum, [, a]) => sum + a.conversions / a.spend, 0);
        for (const [platform, a] of receivers) {
          shift.set(platform, pool * (a.conversions / a.spend) / receiverWeight);
        }
      }
    }

    // Read off the map rather than re-derived from the inputs. The conditions
    // that leave it empty are spread across the block above — no measured
    // channel, exactly one, or every one of them at the median — and a second
    // expression listing them would be a second chance to list them wrongly.
    const reallocationAvailable = shift.size > 0;

    // Does THIS channel report conversion value? Declared here because both the
    // per-channel gate below and the blended ROI further down use the identical
    // predicate — two definitions is how the display started contradicting the
    // ratio's own membership test.
    const reportsRevenue = (a: Agg): boolean => plausibleRevenue(a.value, a.spend);

    // Two allocations, both real: what is being spent now, and the same total
    // redistributed by conversions-per-baht.
    const buildBreakdown = (reallocate: boolean): MMMBudgetItem[] =>
      [...byPlatform.entries()].map(([platform, a]) => {
        const unmeasured = isUnmeasured(a);
        // Only measured channels take part, and only via the bounded shift —
        // an unmeasured channel's spend is not on the table, so it cannot be
        // handed to anyone else. With nothing measured anywhere, everyone
        // keeps what they have rather than being given an invented split.
        const recommended =
          reallocate && !unmeasured
            ? a.spend + (shift.get(platform) ?? 0)
            : a.spend;
        const share = a.spend > 0 ? recommended / a.spend : 1;

        const stage = (key: 'aware' | 'consider' | 'convert' | 'unspecified'): MMMStageBreakdown => {
          const st = a.stages[key];
          // A stage with reach but no spend or conversions is still worth
          // showing — that is exactly the awareness case this data unblocks.
          if (st.spend === 0 && st.conversions === 0 && st.reach === 0 && st.engagements === 0) {
            return emptyStage(key);
          }
          const vol = STAGE_VOLUME[key];
          const measured: Record<'reach' | 'engagements' | 'conversions', number> = {
            reach: st.reach,
            engagements: st.engagements,
            conversions: st.conversions,
          };
          // Stage-appropriate metric first, then anything else that was actually
          // measured. Showing only the first one printed a 0 next to real
          // activity whenever a channel measured that stage differently.
          const order: Array<'reach' | 'engagements' | 'conversions'> = [
            vol.key,
            ...(['reach', 'engagements', 'conversions'] as const).filter((k) => k !== vol.key),
          ];
          const volumes = order
            .filter((k) => measured[k] > 0)
            .map((k) => ({ label: STAGE_VOLUME_LABEL[k], value: measured[k] }));

          return {
            current: st.spend,
            recommended: st.spend * share,
            roi: st.spend > 0 ? st.value / st.spend : 0,
            contribution: st.conversions,
            volume: measured[vol.key],
            volumeLabel: vol.label,
            volumes,
          };
        };

        return {
          name: PLATFORM_NAME[platform] ?? platform,
          unmeasured,
          revenueReported: reportsRevenue(a),
          currentBudget: a.spend,
          recommendedBudget: recommended,
          currentPercent: totalSpend > 0 ? (a.spend / totalSpend) * 100 : 0,
          recommendedPercent: totalSpend > 0 ? (recommended / totalSpend) * 100 : 0,
          stageBreakdown: {
            aware: stage('aware'),
            consider: stage('consider'),
            convert: stage('convert'),
            // Only present when it carries activity — an always-there zero row
            // would put a phantom fourth stage on every channel.
            ...(a.stages.unspecified.spend > 0 ||
            a.stages.unspecified.conversions > 0 ||
            a.stages.unspecified.reach > 0 ||
            a.stages.unspecified.engagements > 0
              ? { unspecified: stage('unspecified') }
              : {}),
          },
        };
      });

    const reallocated = buildBreakdown(true);
    const asIs = buildBreakdown(false);

    // Data coverage, not model confidence — see the doc comment. Counted from
    // the daily metrics table because the campaign rollup cannot answer it.
    const { daysWithData, firstDate } = await fetchMetricDaysCovered(scope, start, end);
    // On 'all' the window opens at a sentinel decades before any campaign, so the
    // denominator becomes the workspace's own history instead: coverage answers
    // "how many of your active days carry data", not "how much of the year 2000".
    const denomStart = start === ALL_TIME_START && firstDate ? firstDate : start;
    const windowDays = Math.max(
      1,
      Math.round((new Date(end).getTime() - new Date(denomStart).getTime()) / 86_400_000) + 1,
    );
    const coverage = Math.min(100, Math.round((daysWithData / windowDays) * 100));

    // Revenue coverage is per channel, exactly like conversion coverage. A
    // channel whose reported value is under 1% of its own spend is not a channel
    // with terrible returns — it is a channel not reporting revenue. Google is
    // the live example: ฿1,632 of value against ฿888,101 of spend (0.18%).
    //
    // Blending regardless produced "ROI 0.44x" for this workspace, which divided
    // TikTok's ฿524,968 — essentially all the revenue there is — by the spend of
    // all four channels including three that report nothing. The ratio is only
    // meaningful over the channels that actually report, so that is what it is
    // computed over, and the page says how much of the spend that covers.

    const revenueChannels = [...byPlatform.entries()].filter(([, a]) => reportsRevenue(a));
    const revenueSpend = revenueChannels.reduce((sum, [, a]) => sum + a.spend, 0);
    const revenueValue = revenueChannels.reduce((sum, [, a]) => sum + a.value, 0);
    const revenueAvailable = revenueChannels.length > 0;
    const roi = revenueAvailable && revenueSpend > 0 ? revenueValue / revenueSpend : 0;
    const revenueCoveragePct = totalSpend > 0 ? Math.round((revenueSpend / totalSpend) * 100) : 0;
    const revenueChannelNames = revenueChannels.map(
      ([platform]) => PLATFORM_NAME[platform] ?? platform,
    );

    const unmeasuredNames = [...byPlatform.entries()]
      .filter(([, a]) => isUnmeasured(a))
      .map(([platform]) => PLATFORM_NAME[platform] ?? platform);

    const unmeasuredNote =
      unmeasuredNames.length > 0
        ? t('mmmRec.unmeasuredNote', { channels: unmeasuredNames.join(t('mmmRec.andJoin')) })
        : '';

    const ranked = [...byPlatform.entries()]
      .filter(([, a]) => a.spend > 0 && a.conversions > 0)
      .sort((x, y) => y[1].conversions / y[1].spend - x[1].conversions / x[1].spend);
    // Localised like every other baht figure on the page — "฿1309.85" beside
    // "฿1,310"-style currency everywhere else read as a different convention.
    const cpa = (a: Agg): string =>
      t('mmmRec.cpaPerConversion', {
        amount: (a.spend / a.conversions).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      });
    const keyInsight =
      ranked.length >= 2
        ? t('mmmRec.insightTwo', {
            cheapest: PLATFORM_NAME[ranked[0][0]] ?? ranked[0][0],
            cheapestCpa: cpa(ranked[0][1]),
            dearest: PLATFORM_NAME[ranked[ranked.length - 1][0]] ?? ranked[ranked.length - 1][0],
            dearestCpa: cpa(ranked[ranked.length - 1][1]),
            days: daysWithData,
            window: windowDays,
          }) + unmeasuredNote
        : ranked.length === 1
          ? t('mmmRec.insightOne', {
              channel: PLATFORM_NAME[ranked[0][0]] ?? ranked[0][0],
              cpa: cpa(ranked[0][1]),
            })
          : t('mmmRec.insightNone');

    // `unmeasuredNote` deliberately NOT repeated here — it rides `keyInsight`,
    // which is the finding. The two used to render 650px apart on /mmm, so the
    // duplication was invisible; they are now adjacent in the hero's "why"
    // slot, and the LINE sentence appeared twice, verbatim, one line below
    // itself. A note about whether a channel is measured is a FINDING, not a
    // description of the reallocation method.
    const explanation =
      t('mmmRec.explanationBase') +
      (revenueAvailable
        ? t('mmmRec.explanationRevenue', {
            channels: revenueChannelNames.join(t('mmmRec.andJoin')),
            pct: revenueCoveragePct,
          })
        : t('mmmRec.explanationNoRevenue'));

    return {
      // The only method this product has. When a second one exists it stamps
      // its own id here, and the page changes nothing.
      engine: 'heuristic',
      computedAt: new Date().toISOString(),
      confidence: coverage,
      revenueAvailable,
      reallocationAvailable,
      revenueCoveragePct,
      revenueSpend,
      baseline: {
        label: t('mmmRec.baselineLabel'),
        revenue: revenueAvailable ? revenueValue : 0,
        spend: totalSpend,
        roi,
        projectedRevenue: revenueAvailable ? revenueValue : 0,
      },
      option1: reallocationAvailable
        ? {
            label: t('mmmRec.option1Label'),
            action: t('mmmRec.option1Action'),
            impact: t('mmmRec.impact', { conversions: formatConversions(totalConversions) }),
            explanation,
            roi,
            projectedRevenue: revenueAvailable ? revenueValue : 0,
            spend: totalSpend,
            // No counterfactual exists without a model, so no delta is claimed.
            change: t('mmmRec.option1Change'),
          }
        : {
            // Nothing moves, so this option states that instead of an
            // instruction. It used to print "shift budget toward the cheapest
            // converting channels" unconditionally, directly above its own
            // keyInsight saying there was nothing to reallocate on — measured on
            // a workspace with ฿604,941 of spend and no conversions.
            //
            // THE MEASURED FIGURES STAY. `spend` is the real total and the
            // breakdowns are the real allocation; only the claim about what to
            // do with them is withdrawn. Routing this through
            // `emptyRecommendations` would have zeroed them, which hides a
            // number the workspace actually has.
            label: t('mmmRec.noneLabel'),
            action: t('mmmRec.noneAction'),
            impact: t('mmmRec.impact', { conversions: formatConversions(totalConversions) }),
            explanation,
            roi,
            projectedRevenue: revenueAvailable ? revenueValue : 0,
            spend: totalSpend,
            change: '',
          },
      option2: {
        label: t('mmmRec.option2Label'),
        action: t('mmmRec.option2Action'),
        impact: t('mmmRec.impact', { conversions: formatConversions(totalConversions) }),
        explanation: t('mmmRec.option2Explanation'),
        roi,
        projectedRevenue: revenueAvailable ? revenueValue : 0,
        spend: totalSpend,
        change: t('mmmRec.option2Change'),
      },
      keyInsight,
      // Empty, not mock: these three drive sections that are not rendered while
      // the page is unmodelled. Filling them with invented rows is what Phase 0
      // removed.
      synergies: [],
      stageTransitions: [],
      funnelFlow: { channels: [] },
      option1BudgetBreakdown: reallocated,
      option2BudgetBreakdown: asIs,
      budgetBreakdown: reallocated,
    };
  } catch (error) {
    logError('MediaIntelligenceService', 'Error fetching MMM recommendations', error);
    return emptyRecommendations(t('mmmRec.reasonLoadFailed'), t);
  }
};


